import { createSelector } from 'reselect';
import { Map } from 'immutable';
import { RootState } from '../../types/redux';
import { DirectSelector } from '../../types/selectors';

// Type guard for Immutable.js state
function isImmutableState(state: RootState): state is { get: (key: string) => unknown } {
  return typeof (state as { get?: (key: string) => unknown }).get === 'function';
}

// Helper function - backward compatible with existing dashboard state
const getDashboard = (state: RootState): Map<string, unknown> => {
  // Handle Immutable.js state structure
  const dashboardState = isImmutableState(state)
    ? (state.get('dashboard') as Map<string, unknown> | undefined)
    : (state as { dashboard?: Map<string, unknown> }).dashboard;

  return dashboardState || Map();
};

/**
| * UI Selectors - Centralized location for all UI state
| * These selectors handle both new UI state and legacy dashboard state for backward compatibility
| */

export const selectSidebarIsMinimized: DirectSelector<boolean> = createSelector(
  [(state: RootState) => state],
  (state) => {
    // Handle Immutable.js state structure
    const uiState = isImmutableState(state)
      ? (state.get('ui') as { dashboard?: { sidebarIsMinimized?: boolean } } | undefined)
      : (state as { ui?: { dashboard?: { sidebarIsMinimized?: boolean } } }).ui;

    // Use modern UI state if available and defined, fallback to legacy for transition period
    if (uiState?.dashboard && uiState.dashboard.sidebarIsMinimized !== undefined) {
      return uiState.dashboard.sidebarIsMinimized;
    }
    return getDashboard(state).get('sidebarIsMinimized', false) as boolean;
  }
);

export const selectSidebarMobileIsOpen: DirectSelector<boolean> = createSelector(
  [(state: RootState) => state],
  (state) => {
    const uiState = isImmutableState(state)
      ? (state.get('ui') as { dashboard?: { sidebarMobileIsOpen?: boolean } } | undefined)
      : (state as { ui?: { dashboard?: { sidebarMobileIsOpen?: boolean } } }).ui;

    return !!uiState?.dashboard?.sidebarMobileIsOpen;
  }
);

export const selectIsLoadingInitialData: DirectSelector<boolean> = createSelector(
  [(state: RootState) => state],
  (state) => {
    // Handle Immutable.js state structure
    const uiState = isImmutableState(state)
      ? (state.get('ui') as { dashboard?: { isLoadingInitialData?: boolean } } | undefined)
      : (state as { ui?: { dashboard?: { isLoadingInitialData?: boolean } } }).ui;

    // Use modern UI state if available and defined, fallback to legacy for transition period
    if (uiState?.dashboard && uiState.dashboard.isLoadingInitialData !== undefined) {
      return uiState.dashboard.isLoadingInitialData;
    }

    // Fallback to legacy dashboard state during transition
    const dashboardState = getDashboard(state);
    const isInitialLoadComplete = dashboardState.get('isInitialLoadComplete', false);
    return !isInitialLoadComplete;
  }
);

// UI state interface for type safety
export interface DashboardUIState {
  sidebarIsMinimized: boolean;
  isLoadingInitialData: boolean;
}

// Combined UI state selectors
export const selectDashboardUIState: DirectSelector<DashboardUIState> = createSelector(
  [
    selectSidebarIsMinimized,
    selectIsLoadingInitialData,
  ],
  (isMinimized, isLoadingInitialData): DashboardUIState => ({
    sidebarIsMinimized: isMinimized,
    isLoadingInitialData,
  })
);

// Derived selectors
export const selectIsDashboardReady: DirectSelector<boolean> = createSelector(
  [selectIsLoadingInitialData],
  (isLoadingInitialData) => !isLoadingInitialData
);
